import React, { useState, useEffect, useContext, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Box, Center, Spinner } from '@chakra-ui/react';
import { GlobalAudioProvider } from './hooks/useGlobalAudioManager';
import usePageTracking from './hooks/usePageTracking';
import { initGA4, setUserId, clearUserId } from './utils/analytics';
import AnalyticsConsent from './components/AnalyticsConsent';
import { ORGANIZE_ENABLED } from './pages/organize/flags';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./pages/LandingPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));
const EmailVerificationNotice = lazy(() => import('./components/EmailVerificationNotice'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));
const ContactSupport = lazy(() => import('./pages/ContactSupport'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const SynthPage = lazy(() => import('./pages/SynthPage'));
const FreePresetsPage = lazy(() => import('./pages/FreePresetsPage'));
// The free Preset Explorer (/library; feature flag REACT_APP_ORGANIZE_ENABLED=1, off by default). Signed-out visitors get a
// guest library (utils/guest.js) that moves into their account when they sign up.
const ExplorerPage = lazy(() => import('./pages/library/ExplorerPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Old SAMP1 pages and assets (matched case-insensitively), redirected to the SON1 section of the home page
const LEGACY_SAMP1_PATHS = [
  '/samp1/*',
  '/products/samp1/*',
  '/products/SAMP1.vst3/*',
  '/audio/samp1/*',
  '/text-to-sample',
];


// Signed-in home: the Preset Explorer (/library), the user's own uploads. There is no dashboard any more.
export const HOME_PATH = '/library';

// The page that sent a visitor to sign in / sign up (state.returnTo or state.from), e.g. /free-presets; null = home.
const authReturnPath = (loc) => loc.state?.returnTo || (loc.state?.from?.pathname ? `${loc.state.from.pathname}${loc.state.from.search || ''}${loc.state.from.hash || ''}` : null);

/** Redirect that keeps the query (?status=... from Stripe) and maps old pages onto /account. */
function KeepQuery({ to, hash = '' }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}${loc.hash || hash}`} replace />;
}
// The SON1 page was removed (2026-11-03): /son1 and /get-started go to the landing's SON1 hero for visitors and to the
// account's SON1 section when signed in (keeping a Stripe return's ?status=...&session_id=...).
function Son1Redirect() {
  const { isAuthenticated } = useContext(AuthContext);
  const loc = useLocation();
  return <Navigate to={isAuthenticated ? `/account${loc.search}#son1` : '/#son1'} replace />;
}
// Old signed-in home (/dashboard) and the landing for an account: the library; a Stripe return (?status=) goes to /account.
function HomeRedirect() {
  const loc = useLocation();
  const stripeReturn = new URLSearchParams(loc.search).get('status');
  return <Navigate to={stripeReturn ? `/account${loc.search}#son1` : HOME_PATH} replace />;
}

// Context for authentication
export const AuthContext = React.createContext();

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [skipAuthRedirect, setSkipAuthRedirect] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize Google Analytics 4
  useEffect(() => {
    initGA4();
  }, []);
  
  // Track page views on route changes
  usePageTracking();
  
  // Set GA4 user ID when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      setUserId(user.id);
    } else {
      clearUserId();
    }
  }, [isAuthenticated, user?.id]);
  
  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const userEmail = localStorage.getItem('userEmail');
      
      if (token && userId) {
        setIsAuthenticated(true);
        setUser({ 
          id: userId,
          name: userName || 'User',
          email: userEmail || '',
        });
      }
      
      // Mark authentication check as complete
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);
  
  // Login function. `to`: where to go next (sign in / sign up pass the page that asked for an account, e.g. /library).
  const login = (token, userId, userData = {}, to = HOME_PATH) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userId);
    
    // Store additional user data
    if (userData.name) localStorage.setItem('userName', userData.name);
    if (userData.email) localStorage.setItem('userEmail', userData.email);
    
    setIsAuthenticated(true);
    setUser({ 
      id: userId,
      ...userData
    });
    navigate(to || HOME_PATH);
  };
  
  // Logout function
  const logout = (options = {}) => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    setIsAuthenticated(false);
    setUser(null);
    if (options.redirect !== false) navigate('/', { replace: true });
  };
  
  // Update user function
  const updateUser = (userData) => {
    // If userData is null, clear the user state completely
    if (userData === null) {
      setUser(null);
      return;
    }
    
    // Only update with non-null values
    setUser(prevUser => {
      const updatedUser = { ...prevUser, ...userData };
      
      // Update localStorage with new user data
      if (userData.name) localStorage.setItem('userName', userData.name);
      if (userData.email) localStorage.setItem('userEmail', userData.email);
      
      return updatedUser;
    });
  };
  
  // Don't render routes until authentication check is complete
  if (isLoading) {
    return (
      <Center h="100vh" bg="var(--app-bg, #06070b)" color="var(--app-text, #f4f6fb)">
        <Spinner size="xl" />
      </Center>
    );
  }
  
  return (
    <GlobalAudioProvider>
      <AuthContext.Provider value={{ 
        isAuthenticated, 
        setIsAuthenticated, 
        user, 
        login, 
        logout, 
        updateUser,
        skipAuthRedirect,
        setSkipAuthRedirect
      }}>
      <Box minH="100vh">
        {/* No beta banner, popup or modal on load anywhere (founder, 2026-10-16) */}
        
        <Suspense fallback={
          <Center h="100vh" bg="var(--app-bg, #06070b)" color="var(--app-text, #f4f6fb)">
            <Spinner size="xl" />
          </Center>
        }>
          <EmailVerificationNotice />
          <Routes>
          {/* Public routes */}
          <Route path="/" element={isAuthenticated ? <HomeRedirect /> : <LandingPage />} />
          <Route path="/signin" element={isAuthenticated ? <Navigate to={authReturnPath(location) || HOME_PATH} /> : <SignInPage />} />
          <Route path="/signup" element={isAuthenticated ? <Navigate to={authReturnPath(location) || HOME_PATH} /> : <SignUpPage />} />
          <Route path="/forgot-password" element={<PasswordResetPage key="forgot" />} />
          <Route path="/verify-email" element={<EmailVerificationPage key={location.hash} />} />
          <Route path="/reset-password" element={<PasswordResetPage key="reset" reset />} />
          <Route path="/set-password" element={<PasswordResetPage key="set" reset setPassword />} />
          <Route path="/contact/enterprise-solutions" element={<Navigate to="/contact/support" replace />} />
          <Route path="/contact-sales" element={<Navigate to="/contact/support" replace />} />
          <Route path="/help" element={<Navigate to="/contact/support" replace />} />
          <Route path="/contact/support" element={<ContactSupport />} />
          <Route path="/feedback" element={<ContactSupport />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* Careers hidden for launch (founder, 2026-10-28): the pages stay in the code, the routes go to About */}
          <Route path="/careers/*" element={<Navigate to="/about" replace />} />
          <Route path="/son1" element={<Son1Redirect />} />
          <Route path="/forma" element={process.env.REACT_APP_FORMA_ENABLED === '1' ? <SynthPage /> : <Navigate to="/" replace />} />
          <Route path="/synth" element={<Navigate to={process.env.REACT_APP_FORMA_ENABLED === '1' ? '/forma' : '/'} replace />} />
          <Route path="/free-presets" element={<FreePresetsPage />} />

          {/* SAMP1 (text-to-sample) was removed: old SAMP1 URLs land on the SON1 section */}
          {LEGACY_SAMP1_PATHS.map((path) => (
            <Route key={path} path={path} element={<Navigate to="/#features" replace />} />
          ))}
          
          {/* Protected routes - User must be authenticated */}
          <Route
            path="/account"
            element={
              isAuthenticated || skipAuthRedirect
                ? <AccountPage />
                : <Navigate to="/signin" replace state={{ from: location }} />
            }
          />
          {/* The dashboard, /profile, /licenses, /get-started, /success and /cancel were folded into /account (2026-10-28).
              Their URLs stay alive: emails, Stripe (success / cancel / portal return) and the plugin ("your Licenses
              page") link to them. */}
          <Route
            path="/dashboard"
            element={isAuthenticated || skipAuthRedirect ? <HomeRedirect /> : <Navigate to="/signin" replace state={{ from: { pathname: HOME_PATH } }} />}
          />
          <Route path="/profile" element={<KeepQuery to="/account" />} />
          <Route path="/licenses" element={<KeepQuery to="/account" hash="#son1" />} />
          <Route path="/get-started" element={<Son1Redirect />} />
          <Route path="/success" element={isAuthenticated ? <KeepQuery to="/account" hash="#son1" /> : <SuccessPage />} />
          <Route path="/cancel" element={<Navigate to="/account?status=cancelled#son1" replace />} />
          <Route
            path="/library"
            element={
              !ORGANIZE_ENABLED
                ? <Navigate to={isAuthenticated ? '/account' : '/'} replace />
                : isAuthenticated || skipAuthRedirect
                  ? <ExplorerPage key="account" />
                  : <ExplorerPage key="guest" guest />
            }
          />
          <Route path="/organize" element={<Navigate to="/library" replace />} />
          {/* Old pricing page: the landing's pricing section */}
          <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Box>
      <AnalyticsConsent />
      </AuthContext.Provider>
    </GlobalAudioProvider>
  );
}

export default App;
